import BaseAPI, { recoverFromNotFound, ROUTES, VERSIONS } from './BaseAPI'
import { isEmail } from '../utils/email'

const { VERSION_FIVE, VERSION_TWO } = VERSIONS
const {
  MESSAGE_FIND,
  MESSAGE_FORWARD,
  MESSAGE_SEND,
  MESSAGE_UPDATE_RECIPIENT_STATUS_MULTI,
} = ROUTES

export default class MessagesAPI extends BaseAPI {
  async send({
    attachment,
    body,
    conversationId,
    counterPartyId,
    deleteOnRead,
    escalate,
    localId,
    metadata,
    priority,
    recipientOrganizationId,
    senderOrganizationId,
    ttl,
  }: {
    attachment?: ?Object|?string,
    body: string,
    conversationId?: ?string,
    counterPartyId?: ?string,
    deleteOnRead?: ?boolean,
    escalate?: ?boolean,
    localId: string,
    metadata?: ?Object,
    priority?: ?number,
    recipientOrganizationId?: ?string,
    senderOrganizationId: string,
    ttl?: ?number,
  }) {
    const { version=VERSION_TWO } = super.getVersion(MESSAGE_SEND)
    const params = {
      'body': body,
    }
    const query = {}
    const files = {}

    if (version === VERSION_TWO) {
      if (isEmail(counterPartyId)) {
        counterPartyId = counterPartyId.toLowerCase()
      }
      params.recipient = counterPartyId
      query.response_format = 'message'

      if (!recipientOrganizationId) recipientOrganizationId = senderOrganizationId
      if (senderOrganizationId) params['sender_organization'] = senderOrganizationId
      if (recipientOrganizationId) params['recipient_organization'] = recipientOrganizationId
    } else if (version === VERSION_FIVE) {
      params.conversation_id = conversationId
    }

    if (metadata) {
      params['data'] = metadata.map(item => ({
        ...item,
        payload: JSON.stringify(item.payload)
      }))
    }
    if (attachment) files['attachment'] = attachment
    if (deleteOnRead) params['dor'] = deleteOnRead
    if (escalate) params['escalate'] = escalate
    if (typeof priority !== 'undefined') params['priority'] = priority
    if (ttl) params['ttl'] = ttl

    try {
      const res = await this.httpClient.post('/:version/message', {
        urlParams: { version },
        data: params,
        files,
        query,
        requestId: localId,
        timeout: !attachment,
      })

      return res.data
    } catch (err) {
      if (err.status !== 304) return Promise.reject(err)
      const id = err.response.headers['TT-X-Message-Id'.toLowerCase()]
      if (!id) return Promise.reject(err)
      return { 'message_id': id }
    }
  }

  async destroy(id: string) {
    try {
      await this.httpClient.del('/v2/message/:id', { urlParams: { id } })
      return true
    } catch (e) {
      return false
    }
  }

  async startTyping(recipientId: string, senderId: ?string) {
    const data = {
      recipient: recipientId,
    }

    if (senderId) data['proxy_token'] = senderId

    await this.httpClient.post('/v2/message/typing', { data })
  }

  async stopTyping(recipientId: string, senderId: ?string) {
    if (senderId) {
      await this.httpClient.del('/v2/message/typing/:recipientId/:senderId', {
        urlParams: {
          recipientId,
          senderId,
        }
      })
    } else {
      await this.httpClient.del('/v2/message/typing/:recipientId', {
        urlParams: { recipientId }
      })
    }
  }

  async forward(id: string, counterPartyId?: ?string, {
    conversationId,
    localId,
    metadata,
    priority,
    recipientOrganizationId,
    senderOrganizationId,
  }: {
    conversationId?: ?string,
    localId: string,
    metadata?: ?Object,
    priority?: ?number,
    recipientOrganizationId?: ?string,
    senderOrganizationId?: ?string,
  }) {
    const { version=VERSION_TWO } = super.getVersion(MESSAGE_FORWARD)
    const params = {}

    if (version === VERSION_TWO) {
      params.recipient = counterPartyId

      if (!recipientOrganizationId) recipientOrganizationId = senderOrganizationId
      if (senderOrganizationId) params['sender_organization'] = senderOrganizationId
      if (recipientOrganizationId) params['recipient_organization'] = recipientOrganizationId
    } else if (version === VERSION_FIVE) {
      params.conversation_id = conversationId
    }

    if (metadata) {
      params['data'] = metadata.map(item => ({
        ...item,
        payload: JSON.stringify(item.payload)
      }))
    }
    if (typeof priority !== 'undefined') params['priority'] = priority

    const res = await this.httpClient.post('/:version/message/:id/forward', {
      urlParams: { id, version },
      data: params,
      requestId: localId
    })

    if (version === VERSION_TWO) {
      const message = {
        'message_id': res.getHeader('TT-X-Message-Id'),
      }

      return message
    } else {
      return res.data
    }
  }

  @recoverFromNotFound()
  async find(id: string) {
    const { version=VERSION_TWO } = super.getVersion(MESSAGE_FIND)

    const res = await this.httpClient.get('/:version/message/:id', {
      urlParams: {
        id,
        version,
      },
    })

    return res.data
  }

  @recoverFromNotFound()
  async findRecipientStatus(id: string) {
    const res = await this.httpClient.get('/v2/message/:id/status', { urlParams: { id } })
    return res.data
  }

  async findRecipientStatusMulti(ids: string[]) {
    const res = await this.httpClient.post('/v2/get_bulk_message_status', {
      data: { 'messages': ids },
    })

    return res.data['bulk_message_statuses']
  }

  async updateRecipientStatusMulti(changes) {
    const { version=VERSION_TWO } = super.getVersion(MESSAGE_UPDATE_RECIPIENT_STATUS_MULTI)

    const res = await this.httpClient.post('/:version/message/status', {
      urlParams: { version },
      data: changes,
    })

    return res.data
  }
}
